import { selectFiles } from '@app/components/FileUpload/modules/fileUpload'
import { Address } from '@app/models/Address'
import {
  AssociatedContact,
  IAssociatedContact,
} from '@app/models/AssociatedContact'
import { Attachment } from '@app/models/Attachment'
import { DevelopmentSite, IDevelopmentSite } from '@app/models/DevelopmentSite'
import { DevelopmentSiteStatus } from '@app/models/enums'
import { FileUploadAttachment } from '@app/models/FileUploadAttachment'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Map, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

export const ATTACHMENTS_IDENTIFIER = 'development-site-attachment-editor'
export const PHOTOS_IDENTIFIER = 'development-site-photos-editor'

// -------------------------
// STATE
// -------------------------

export interface IDevelopmentSiteEditState {
  readonly error?: string
  readonly isLoading: boolean
  readonly isReady: boolean
  readonly isSaving: boolean
  readonly newTeamMembers: List<IAssociatedContact>
  readonly newTeamName: string
  readonly showTeamModal: boolean
  readonly site: IDevelopmentSite
}

const DevelopmentSiteEditStateRecord = Record({
  error: undefined,
  isLoading: false,
  isReady: false,
  isSaving: false,
  newTeamMembers: List<AssociatedContact>(),
  newTeamName: '',
  showTeamModal: false,
  site: new DevelopmentSite(),
})

export class DevelopmentSiteEditState extends DevelopmentSiteEditStateRecord
  implements IDevelopmentSiteEditState {
  public static fromJS(initialState?: Partial<IDevelopmentSiteEditState>) {
    if (!initialState) {
      return new DevelopmentSiteEditState()
    }

    const { newTeamMembers, site, ...rest } = initialState

    return new DevelopmentSiteEditState({
      newTeamMembers: Array.isArray(newTeamMembers)
        ? List(newTeamMembers.map((l) => AssociatedContact.fromJS(l)))
        : List<AssociatedContact>(),
      site: DevelopmentSite.fromJS(site),
      ...rest,
    })
  }

  public readonly error?: string
  public readonly isLoading: boolean
  public readonly isReady: boolean
  public readonly isSaving: boolean
  public readonly newTeamMembers: List<IAssociatedContact>
  public readonly newTeamName: string
  public readonly showTeamModal: boolean
  public readonly site: IDevelopmentSite

  constructor(props?: Partial<DevelopmentSiteEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  AddNewTeam = 'DEVELOPMENT_SITE_EDIT::ADD_NEW_TEAM',
  BeginAddTeam = 'DEVELOPMENT_SITE_EDIT::BEGIN_ADD_TEAM',
  CancelAddTeam = 'DEVELOPMENT_SITE_EDIT::CANCEL_ADD_TEAM',
  ChangeNewTeamName = 'DEVELOPMENT_SITE_EDIT::CHANGE_NEW_TEAM_NAME',
  EndAddTeam = 'DEVELOPMENT_SITE_EDIT::END_ADD_TEAM',
  LoadAddDevelopmentSite = 'DEVELOPMENT_SITE_EDIT::LOAD_ADD_DEVELOPMENT_SITE',
  LoadEditDevelopmentSite = 'DEVELOPMENT_SITE_EDIT::LOAD_EDIT_DEVELOPMENT_SITE',
  LoadDevelopmentSiteFailure = 'DEVELOPMENT_SITE_EDIT::LOAD_DEVELOPMENT_SITE_FAILURE',
  LoadDevelopmentSiteRequest = 'DEVELOPMENT_SITE_EDIT::LOAD_DEVELOPMENT_SITE_REQUEST',
  LoadDevelopmentSiteSuccess = 'DEVELOPMENT_SITE_EDIT::LOAD_DEVELOPMENT_SITE_SUCCESS',
  ResetState = 'DEVELOPMENT_SITE_EDIT::RESET_STATE',
  SaveDevelopmentSite = 'DEVELOPMENT_SITE_EDIT::SAVE_DEVELOPMENT_SITE',
  SaveDevelopmentSiteFailure = 'DEVELOPMENT_SITE_EDIT::SAVE_DEVELOPMENT_SITE_FAILURE',
  SaveDevelopmentSiteRequest = 'DEVELOPMENT_SITE_EDIT::SAVE_DEVELOPMENT_SITE_REQUEST',
  SaveDevelopmentSiteSuccess = 'DEVELOPMENT_SITE_EDIT::SAVE_DEVELOPMENT_SITE_SUCCESS',
  SetAddress = 'DEVELOPMENT_SITE_EDIT::SET_ADDRESS',
  SetConstructionPrice = 'DEVELOPMENT_SITE_EDIT::SET_CONSTRUCTION_PRICE',
  SetMainPhoto = 'DEVELOPMENT_SITE_EDIT::SET_MAIN_PHOTO',
  SetName = 'DEVELOPMENT_SITE_EDIT::SET_NAME',
  SetNewTeamMembers = 'DEVELOPMENT_SITE_EDIT::SET_NEW_TEAM_MEMBERS',
  SetNewTeamName = 'DEVELOPMENT_SITE_EDIT::SET_NEW_TEAM_NAME',
  SetSalesPrice = 'DEVELOPMENT_SITE_EDIT::SET_SALES_PRICE',
  SetStatus = 'DEVELOPMENT_SITE_EDIT::SET_STATUS',
  SetTeamRoleMembers = 'DEVELOPMENT_SITE_EDIT::SET_TEAM_ROLE_MEMBERS',
  ToggleShowAddTeamModal = 'DEVELOPMENT_SITE_EDIT::TOGGLE_SHOW_ADD_TEAM_MODAL',
}

interface IAddNewTeamAction {
  type: ActionTypes.AddNewTeam
}

interface IBeginAddTeamAction {
  type: ActionTypes.BeginAddTeam
}

interface ICancelAddTeamAction {
  type: ActionTypes.CancelAddTeam
}

interface IChangeNewTeamNameAction {
  name: string
  type: ActionTypes.ChangeNewTeamName
}

interface IEndAddTeamAction {
  type: ActionTypes.EndAddTeam
}

interface ILoadAddDevelopmentSiteAction {
  clientSlug: string
  type: ActionTypes.LoadAddDevelopmentSite
}

interface ILoadEditDevelomentSiteAction {
  clientSlug: string
  siteSlug: string
  type: ActionTypes.LoadEditDevelopmentSite
}

interface ILoadDevelopmentSiteFailureAction {
  error: string
  type: ActionTypes.LoadDevelopmentSiteFailure
}

interface ILoadDevelopmentSiteRequestAction {
  type: ActionTypes.LoadDevelopmentSiteRequest
}

interface ILoadDevelopmentSiteSuccessAction {
  site: DevelopmentSite
  type: ActionTypes.LoadDevelopmentSiteSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISaveDevelopmentSiteAction {
  clientSlug: string
  site: IDevelopmentSite
  type: ActionTypes.SaveDevelopmentSite
}

interface ISaveDevelopmentSiteFailureAction {
  error: string
  type: ActionTypes.SaveDevelopmentSiteFailure
}

interface ISaveDevelopmentSiteRequestAction {
  type: ActionTypes.SaveDevelopmentSiteRequest
}

interface ISaveDevelopmentSiteSuccessAction {
  type: ActionTypes.SaveDevelopmentSiteSuccess
}

interface ISetAddressAction {
  address: Address
  type: ActionTypes.SetAddress
}

interface ISetConstructionPriceAction {
  constructionPrice: number
  type: ActionTypes.SetConstructionPrice
}

interface ISetMainPhotoAction {
  photoId?: string
  type: ActionTypes.SetMainPhoto
}

interface ISetNameAction {
  name: string
  type: ActionTypes.SetName
}

interface ISetNewTeamMembersAction {
  teamMembers: List<IAssociatedContact>
  type: ActionTypes.SetNewTeamMembers
}

interface ISetNewTeamNameAction {
  name: string
  type: ActionTypes.SetNewTeamName
}

interface ISetSalesPriceAction {
  salesPrice: number
  type: ActionTypes.SetSalesPrice
}

interface ISetStatusAction {
  status: string
  type: ActionTypes.SetStatus
}

interface ISetTeamRoleMembersAction {
  key: string
  members: List<AssociatedContact>
  type: ActionTypes.SetTeamRoleMembers
}

interface IToggleShowAddTeamModalAction {
  show: boolean
  type: ActionTypes.ToggleShowAddTeamModal
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IAddNewTeamAction
  | ILoadDevelopmentSiteFailureAction
  | ILoadDevelopmentSiteRequestAction
  | ILoadDevelopmentSiteSuccessAction
  | IResetStateAction
  | ISaveDevelopmentSiteFailureAction
  | ISaveDevelopmentSiteRequestAction
  | ISaveDevelopmentSiteSuccessAction
  | ISetAddressAction
  | ISetConstructionPriceAction
  | ISetMainPhotoAction
  | ISetNameAction
  | ISetNewTeamMembersAction
  | ISetNewTeamNameAction
  | ISetSalesPriceAction
  | ISetStatusAction
  | ISetTeamRoleMembersAction
  | IToggleShowAddTeamModalAction

// -------------------------
// ACTION CREATORS
// -------------------------
function addNewTeam(): IAddNewTeamAction {
  return { type: ActionTypes.AddNewTeam }
}

function beginAddTeam(): IBeginAddTeamAction {
  return { type: ActionTypes.BeginAddTeam }
}

function cancelAddTeam(): ICancelAddTeamAction {
  return { type: ActionTypes.CancelAddTeam }
}

function endAddTeam(): IEndAddTeamAction {
  return { type: ActionTypes.EndAddTeam }
}

function loadAddDevelopmentSite(
  clientSlug: string,
): ILoadAddDevelopmentSiteAction {
  return {
    clientSlug,
    type: ActionTypes.LoadAddDevelopmentSite,
  }
}

function loadEditDevelopmentSite(
  clientSlug: string,
  siteSlug: string,
): ILoadEditDevelomentSiteAction {
  return {
    clientSlug,
    siteSlug,
    type: ActionTypes.LoadEditDevelopmentSite,
  }
}

function loadDevelopmentSiteFailure(
  error: string,
): ILoadDevelopmentSiteFailureAction {
  return {
    error,
    type: ActionTypes.LoadDevelopmentSiteFailure,
  }
}

function loadDevelopmentSiteRequest(): ILoadDevelopmentSiteRequestAction {
  return { type: ActionTypes.LoadDevelopmentSiteRequest }
}

function loadDevelopmentSiteSuccess(
  site: DevelopmentSite,
): ILoadDevelopmentSiteSuccessAction {
  return {
    site,
    type: ActionTypes.LoadDevelopmentSiteSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function saveDevelopmentSite(
  clientSlug: string,
  site: IDevelopmentSite,
): ISaveDevelopmentSiteAction {
  return {
    clientSlug,
    site,
    type: ActionTypes.SaveDevelopmentSite,
  }
}

function saveDevelopmentSiteFailure(
  error: string,
): ISaveDevelopmentSiteFailureAction {
  return {
    error,
    type: ActionTypes.SaveDevelopmentSiteFailure,
  }
}

function saveDevelopmentSiteRequest(): ISaveDevelopmentSiteRequestAction {
  return {
    type: ActionTypes.SaveDevelopmentSiteRequest,
  }
}

function saveDevelopmentSiteSuccess(): ISaveDevelopmentSiteSuccessAction {
  return {
    type: ActionTypes.SaveDevelopmentSiteSuccess,
  }
}

function setAddress(address: Address): ISetAddressAction {
  return {
    address,
    type: ActionTypes.SetAddress,
  }
}

function setConstructionPrice(
  constructionPrice: number,
): ISetConstructionPriceAction {
  return {
    constructionPrice,
    type: ActionTypes.SetConstructionPrice,
  }
}

function setMainPhoto(photoId?: string): ISetMainPhotoAction {
  return {
    photoId,
    type: ActionTypes.SetMainPhoto,
  }
}

function setName(name: string): ISetNameAction {
  return {
    name,
    type: ActionTypes.SetName,
  }
}

function setNewTeamMembers(
  teamMembers: List<IAssociatedContact>,
): ISetNewTeamMembersAction {
  return {
    teamMembers,
    type: ActionTypes.SetNewTeamMembers,
  }
}

function setNewTeamName(name: string): ISetNewTeamNameAction {
  return {
    name,
    type: ActionTypes.SetNewTeamName,
  }
}

function setSalesPrice(salesPrice: number): ISetSalesPriceAction {
  return {
    salesPrice,
    type: ActionTypes.SetSalesPrice,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: ActionTypes.SetStatus,
  }
}

function setTeamRoleMembers(
  key: string,
  members: List<AssociatedContact>,
): ISetTeamRoleMembersAction {
  return {
    key,
    members,
    type: ActionTypes.SetTeamRoleMembers,
  }
}

function toggleShowAddTeamModal(show: boolean): IToggleShowAddTeamModalAction {
  return {
    show,
    type: ActionTypes.ToggleShowAddTeamModal,
  }
}

export const actionCreators = {
  addNewTeam,
  beginAddTeam,
  cancelAddTeam,
  endAddTeam,
  loadAddDevelopmentSite,
  loadEditDevelopmentSite,
  resetState,
  saveDevelopmentSite,
  setAddress,
  setConstructionPrice,
  setMainPhoto,
  setName,
  setNewTeamMembers,
  setNewTeamName,
  setSalesPrice,
  setStatus,
  setTeamRoleMembers,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new DevelopmentSiteEditState()

export const reducer: Reducer<DevelopmentSiteEditState> = (
  state: DevelopmentSiteEditState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.AddNewTeam:
      return state.setIn(
        ['site', 'team'],
        List<AssociatedContact>([
          ...state.site.team.toArray(),
          {
            name: state.newTeamName,
            members: state.newTeamMembers,
          },
        ]),
      ) as DevelopmentSiteEditState

    case ActionTypes.LoadDevelopmentSiteFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false)
        .set('isReady', false) as DevelopmentSiteEditState

    case ActionTypes.LoadDevelopmentSiteRequest:
      return state
        .set('isLoading', true)
        .set('isReady', false) as DevelopmentSiteEditState

    case ActionTypes.LoadDevelopmentSiteSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('isReady', true)
        .set('site', action.site) as DevelopmentSiteEditState

    case ActionTypes.ResetState:
      return state
        .set('error', undefined)
        .set('isReady', false)
        .set('site', new DevelopmentSite()) as DevelopmentSiteEditState

    case ActionTypes.SaveDevelopmentSiteFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as DevelopmentSiteEditState

    case ActionTypes.SaveDevelopmentSiteRequest:
      return state.set('isSaving', true) as DevelopmentSiteEditState

    case ActionTypes.SaveDevelopmentSiteSuccess:
      return state
        .set('error', undefined)
        .set('isSaving', false) as DevelopmentSiteEditState

    case ActionTypes.SetAddress:
      return state.setIn(
        ['site', 'address'],
        action.address,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetConstructionPrice:
      return state.setIn(
        ['site', 'constructionPrice'],
        action.constructionPrice,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetMainPhoto:
      return state.setIn(
        ['site', 'coverPhotoId'],
        action.photoId,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetName:
      return state.setIn(
        ['site', 'name'],
        action.name,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetNewTeamMembers:
      return state.set(
        'newTeamMembers',
        action.teamMembers,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetNewTeamName:
      return state.set('newTeamName', action.name) as DevelopmentSiteEditState

    case ActionTypes.SetSalesPrice:
      return state.setIn(
        ['site', 'salesPrice'],
        action.salesPrice,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetStatus:
      return state.setIn(
        ['site', 'status'],
        action.status,
      ) as DevelopmentSiteEditState

    case ActionTypes.SetTeamRoleMembers: {
      const index = state.site.team.findIndex(
        (t) => t !== undefined && t.key === action.key,
      )
      if (index >= 0) {
        return state.setIn(
          ['site', 'team', index, 'members'],
          action.members,
        ) as DevelopmentSiteEditState
      } else {
        return state
      }
    }

    case ActionTypes.ToggleShowAddTeamModal: {
      return state.set('showTeamModal', action.show) as DevelopmentSiteEditState
    }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* beginAddTeamSaga() {
  try {
    // initialize empty team
    yield put(toggleShowAddTeamModal(true))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* cancelAddTeamSaga() {
  try {
    yield put(toggleShowAddTeamModal(false))
    yield put(setNewTeamMembers(List<AssociatedContact>()))
    yield put(setNewTeamName(''))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* changeNewTeamNameSaga(action: IChangeNewTeamNameAction) {
  try {
    yield put(setNewTeamName(action.name))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* endAddTeamSaga() {
  try {
    yield put(addNewTeam())
    yield put(toggleShowAddTeamModal(false))
    yield put(setNewTeamMembers(List<AssociatedContact>()))
    yield put(setNewTeamName(''))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadAddDevelopmentSiteSaga(action: ILoadAddDevelopmentSiteAction) {
  try {
    yield put(loadDevelopmentSiteRequest())

    const result = yield Promise.all([
      makeAuthenticatedRequest(`/api/DevelopmentClients/${action.clientSlug}`),
      makeAuthenticatedRequest(
        '/api/DevelopmentClients/undefined/Sites/template',
      ),
    ])

    if (result[0].success === true && result[1].success === true) {
      const client = result[0].entity
      const site = result[1].entity
      site.client = {
        clientId: client.id,
        name: client.name,
      }
      site.status = DevelopmentSiteStatus.Potential
      yield put(loadDevelopmentSiteSuccess(DevelopmentSite.fromJS(site)))
    } else {
      yield put(loadDevelopmentSiteFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadDevelopmentSiteFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadEditDevelopmentSiteSaga(action: ILoadEditDevelomentSiteAction) {
  try {
    yield put(loadDevelopmentSiteRequest())

    const clientResult = yield makeAuthenticatedRequest(
      `/api/DevelopmentClients/${action.clientSlug}`,
    )

    if (clientResult.success === true) {
      const siteResult = yield makeAuthenticatedRequest(
        `/api/DevelopmentClients/${clientResult.entity.id}/Sites/${action.siteSlug}`,
      )

      if (siteResult.success === true) {
        yield put(
          loadDevelopmentSiteSuccess(DevelopmentSite.fromJS(siteResult.entity)),
        )
      }
    } else {
      yield put(loadDevelopmentSiteFailure(clientResult.message))
      yield put(errorToast('Oops!', clientResult.message))
    }
  } catch (err) {
    yield put(loadDevelopmentSiteFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* saveDevelopmentSiteSaga(action: ISaveDevelopmentSiteAction) {
  try {
    yield put(saveDevelopmentSiteRequest())

    const photos: Map<string, FileUploadAttachment> = yield select(
      selectFiles(PHOTOS_IDENTIFIER),
    )
    const photosList = photos.toArray().reduce((list, fileUpload) => {
      if (fileUpload === undefined || fileUpload.error) {
        return list
      }

      return list.push(fileUpload.attachment)
    }, List<Attachment>())

    const attachments: Map<string, FileUploadAttachment> = yield select(
      selectFiles(ATTACHMENTS_IDENTIFIER),
    )
    const attachmentsList = attachments
      ? attachments.toArray().reduce((list, fileUpload) => {
          if (fileUpload === undefined || fileUpload.error) {
            return list
          }

          return list.push(fileUpload.attachment)
        }, List<Attachment>())
      : List<Attachment>()

    const site = (action.site as DevelopmentSite)
      .set('attachments', attachmentsList)
      .set('photos', photosList)
      .toJS()

    const reqUrl = site.id
      ? `/api/DevelopmentClients/${site.client.clientId}/Sites/${site.id}`
      : `/api/DevelopmentClients/${site.client.clientId}/Sites`

    const reqOpts: IRequestOptions = {
      body: JSON.stringify(site),
      method: site.id ? RequestMethod.PUT : RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(saveDevelopmentSiteSuccess())
      yield put(
        push(
          `/development/client/${action.clientSlug}/site/${result.entity.slug}`,
        ),
      )
      yield put(
        successToast('Success!', `${site.client.name} - ${site.name} saved`),
      )
    } else {
      yield put(saveDevelopmentSiteFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(saveDevelopmentSiteFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchBeginAddTeam() {
  yield takeLatest(ActionTypes.BeginAddTeam, beginAddTeamSaga)
}

function* watchCancelAddTeam() {
  yield takeLatest(ActionTypes.CancelAddTeam, cancelAddTeamSaga)
}

function* watchChangeNewTeamName() {
  yield takeLatest(ActionTypes.ChangeNewTeamName, changeNewTeamNameSaga)
}

function* watchEndAddTeam() {
  yield takeLatest(ActionTypes.EndAddTeam, endAddTeamSaga)
}

function* watchLoadAddDevelopmentSite() {
  yield takeLatest(
    ActionTypes.LoadAddDevelopmentSite,
    loadAddDevelopmentSiteSaga,
  )
}

function* watchLoadEditDevelopmentSite() {
  yield takeLatest(
    ActionTypes.LoadEditDevelopmentSite,
    loadEditDevelopmentSiteSaga,
  )
}

function* watchSaveDevelopmentSite() {
  yield takeLatest(ActionTypes.SaveDevelopmentSite, saveDevelopmentSiteSaga)
}

export function* developmentSiteEditRootSaga() {
  yield fork(watchBeginAddTeam)
  yield fork(watchCancelAddTeam)
  yield fork(watchChangeNewTeamName)
  yield fork(watchEndAddTeam)
  yield fork(watchLoadAddDevelopmentSite)
  yield fork(watchLoadEditDevelopmentSite)
  yield fork(watchSaveDevelopmentSite)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.developmentSiteEdit

export const selectShowTeamModal = () =>
  createSelector(selectModuleState(), (mState) => mState.showTeamModal)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsReady = () =>
  createSelector(selectModuleState(), (mState) => mState.isReady)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectNewTeamMembers = () =>
  createSelector(selectModuleState(), (mState) => mState.newTeamMembers)

export const selectNewTeamName = () =>
  createSelector(selectModuleState(), (mState) => mState.newTeamName)

export const selectSite = () =>
  createSelector(selectModuleState(), (mState) => mState.site)
